import { createTheme, ThemeProvider } from "@mui/material";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import { styled } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import Loader from "_shared/_views/loading/loader";
import Guid from "_shared/guids/guid";
import AttorneyHubLogo from "_shared/_views/assets/attorney-hub-logo.svg";
import UserEntityMemberInvitationAPIService from "legal-entities/invitations/member-invitations/email-based/_api/user-entity-member-invitation-api-service";
import UserEntityMemberInvitationAcceptanceForm from "legal-entities/invitations/member-invitations/email-based/_views/forms/user-entity-member-invitation-acceptance-form";
import UserEntityMemberInvitation, {
  UserMemberInvitationNotFoundError,
} from "legal-entities/invitations/member-invitations/email-based/user-entity-member-invitation";
import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router";
import { useSession } from "users/__session/session-context";

const FormContainer = styled(Box)(({ theme }) => ({
  backgroundColor: "#404558",
  boxSizing: "border-box",
  height: "100%",
  overflow: "auto",
  padding: theme.spacing(5, 1),
  textAlign: "center",
  width: "100%",
}));
const Logo = styled("img")(({ theme }) => ({
  marginBottom: theme.spacing(4),
  maxWidth: "90%",
}));
const InvitationForm = styled(Container)(({ theme }) => ({
  color: theme.palette.common.white,
  padding: theme.spacing(2),
  position: "relative",
  width: "100%",
}));
const InvitationFormTitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(1),
}));
const InvitationFormInstructions = styled(Typography)(({ theme }) => ({
  color: theme.palette.common.white,
  marginBottom: theme.spacing(2),
  "& a": {
    color: theme.palette.common.white,
    textDecoration: "underline",
  },
}));

type UserEntityMemberInvitationAcceptancePageProps = {
  className?: string;
  invitationId?: Guid;
};

export default function UserEntityMemberInvitationAcceptancePage(
  props: Readonly<UserEntityMemberInvitationAcceptancePageProps>,
) {
  const { className } = props;

  const [invitation, setInvitation] = useState<UserEntityMemberInvitation>();
  const [loading, setLoading] = useState<boolean>(true);
  const params = useParams<{ invitationId: string }>();
  const theme = createTheme();
  const session = useSession();

  const loadInvitation = useCallback(async (abortController: AbortController) => {
    try {
      let invitationId: Guid | undefined = undefined;

      if (props.invitationId) invitationId = props.invitationId;
      else if (params.invitationId) invitationId = new Guid(params.invitationId);

      if (!invitationId) throw new Error("Invitation ID was not provided");

      const service = new UserEntityMemberInvitationAPIService(session);
      const returnedInvitation = await service.getUserEntityMemberInvitationById(invitationId, abortController);
      setInvitation(returnedInvitation);
    } catch (error) {
      if (error instanceof UserMemberInvitationNotFoundError) {
        console.warn(error.message);
      } else {
        console.error(error);
      }
    } finally {
      setLoading(false);
    }
  }, [props.invitationId, params.invitationId, session]);

  useEffect(() => {
    let abortController = new AbortController();
    queueMicrotask(() => {
      void loadInvitation(abortController);
    });

    return () => {
      abortController.abort();
      abortController = new AbortController();
    };
  }, [loadInvitation]);

  function invitationNotFoundMessage() {
    return (
      <InvitationForm className={className}>
        <InvitationFormTitle variant="h4">Invitation Not Found</InvitationFormTitle>
        <InvitationFormInstructions>
          The invitation couldn&apos;t be found.
          <br />
          If you believe this is in error please contact{" "}
          <a href="mailto:support@attorneyhub.com" target="_blank" rel="noreferrer">
            support@attorneyhub
          </a>
          .<br />
          You can close this tab
        </InvitationFormInstructions>
      </InvitationForm>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <FormContainer className={className}>
        <Logo src={AttorneyHubLogo} alt="AttorneyHub Logo" />
        {loading && <Loader />}
        {!invitation && !loading && invitationNotFoundMessage()}
        {invitation && <UserEntityMemberInvitationAcceptanceForm invitation={invitation} />}
      </FormContainer>
    </ThemeProvider>
  );
}
